<template>
    <div
        class="relative overflow-hidden font-bold text-center py-8 text-white"
        style="padding-top: 420px; margin-top: -420px;"
    >
        <img
            :src="trunkSrc"
            alt=""
            aria-hidden="true"
            class="absolute pointer-events-none nature-deco"
            style="right: 0; bottom: 0; width: 187px; height: auto; image-rendering: pixelated;"
        >
        <img
            :src="trunk2Src"
            alt=""
            aria-hidden="true"
            class="absolute pointer-events-none nature-deco"
            style="left: 0; bottom: 0; width: 78px; height: auto; image-rendering: pixelated;"
        >
        <div class="absolute inset-0 pointer-events-none" aria-hidden="true">
            <img
                v-for="(leaf, index) in litterLeaves"
                class="nature-deco"
                :key="'litter-' + index"
                :src="isLight ? leaf.sprite.day : leaf.sprite.night"
                alt=""
                :style="{
                    position: 'absolute',
                    bottom: leaf.bottom,
                    left: leaf.left,
                    width: (40 * leaf.scale) + 'px',
                    height: (40 * leaf.scale) + 'px',
                    imageRendering: 'pixelated',
                    transform: 'rotate(' + leaf.rotation + 'deg)' + (leaf.mirror ? ' scaleX(-1)' : ''),
                }"
            >
        </div>
        <span class="relative z-10">{{ t.footer }}</span>
    </div>
</template>

<script>
import { themeState } from '@/lib/theme';
import { languageState } from '@/lib/language';
import { translations } from '@/config/translations';
import treeTrunk from '@/assets/tree-trunk.svg';
import treeTrunk2 from '@/assets/tree-trunk-2.svg';
import treeTrunkNight from '@/assets/tree-trunk-night.svg';
import treeTrunk2Night from '@/assets/tree-trunk-2-night.svg';
import { LEAF_SPRITES } from '@/lib/leafSprites';

// Hojas quietas (sin animación) amontonadas contra el borde inferior del
// footer, como si el viento las hubiese apilado ahí ("leaf litter" en el
// piso). Son cientos, así que se generan proceduralmente en vez de a mano;
// el PRNG con seed fija hace que el desparramo sea siempre el mismo entre
// recargas en vez de cambiar en cada render.

function seededRandom(seed) {
    let state = seed
    return function next() {
        state = (state + 0x6D2B79F5) | 0
        let t = Math.imul(state ^ (state >>> 15), 1 | state)
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}

// Genera una franja completa (0-100%) de hojas. Se llama dos veces con
// semillas distintas (ver generateLitter) para duplicar la cantidad total:
// dos capas independientes se superponen y, al ser aleatorias por separado,
// una tapa los huecos que deja la otra en vez de sumarse a los mismos huecos.
// referenceWidthPx es el ancho real del contenedor (viewport): al usarlo en
// vez de un valor fijo, cada hoja ocupa más % de ancho en pantallas angostas
// y el loop corta antes, así la cantidad de hojas se adapta al ancho en vez
// de quedar siempre fija (en mobile se veían amontonadas/exceso de hojas).
function generateLitterLayer(seed, verticalShiftPx, referenceWidthPx) {
    const random = seededRandom(seed)
    const leaves = []
    // Colocación secuencial: se calcula el ancho REAL del bounding box ya
    // rotado de cada hoja (lado * (|cos| + |sin| del ángulo)) y se coloca
    // cada una pegada al borde derecho real de la anterior, con un solape
    // (no solo un toque) porque la hoja es un dibujo pixel-art hueco que no
    // llena todo su bounding box — sin solape real quedan huecos visuales
    // aunque las cajas se toquen. El cap de superposición ahora es más
    // permisivo (hasta 3-4 hojas), así que se prioriza cerrar huecos.
    let cursorPct = -2
    while (cursorPct < 102) {
        const scale = 0.8 + random() * 0.3
        const rotationDeg = Math.round(random() * 360)
        const rotationRad = (rotationDeg * Math.PI) / 180
        const baseWidthPx = 24 * scale
        const rotatedWidthPx = baseWidthPx * (Math.abs(Math.cos(rotationRad)) + Math.abs(Math.sin(rotationRad)))
        const halfWidthPct = (rotatedWidthPx / referenceWidthPx) * 100 / 2
        const overlapPct = halfWidthPct * (0.4 + random() * 0.25)
        const left = cursorPct + halfWidthPct - overlapPct
        // 25% de las hojas quedan 2-3px más altas que el resto, para romper
        // la línea pareja del borde inferior.
        const raisedPx = random() < 0.25 ? 2 + random() * 1 : 0
        leaves.push({
            left: Math.max(0, Math.min(100, left)).toFixed(2) + '%',
            // La hoja está centrada en su sprite de 40px (con aire transparente
            // alrededor), y rota sobre ese centro: se ubica el CENTRO del
            // sprite apenas por encima del piso para que la hoja siempre
            // apoye, gire como gire. Lo que sobra queda recortado por el
            // overflow-hidden del footer.
            bottom: (-20 * scale + random() * 4 + verticalShiftPx + raisedPx).toFixed(2) + 'px',
            rotation: rotationDeg,
            mirror: random() > 0.5,
            sprite: LEAF_SPRITES[Math.floor(random() * LEAF_SPRITES.length)],
            scale,
        })
        cursorPct = left + halfWidthPct
    }
    return leaves
}

function generateLitter(referenceWidthPx) {
    return [
        ...generateLitterLayer(1337, 0, referenceWidthPx),
        ...generateLitterLayer(4242, -2, referenceWidthPx),
    ]
}

export default {
  name: 'Simple-Footer',
  data() {
    return {
        languageState,
        themeState,
        viewportWidth: window.innerWidth,
        resizeTimeout: null,
    }
  },
  computed: {
    // Los troncos ya vienen coloreados (día / noche), igual que las ramas de Experience.vue.
    trunkSrc() {
      return this.themeState.value === 'light' ? treeTrunk : treeTrunkNight;
    },
    trunk2Src() {
      return this.themeState.value === 'light' ? treeTrunk2 : treeTrunk2Night;
    },
    t() {
      return translations[this.languageState.value]
    },
    isLight() {
      return this.themeState.value === 'light'
    },
    litterLeaves() {
      return generateLitter(this.viewportWidth)
    },
  },
  methods: {
    onResize() {
        // Debounce con setTimeout (no requestAnimationFrame): el conteo de
        // hojas no necesita sincronizarse con un frame de pintado, y así
        // se actualiza igual aunque la pestaña esté en background.
        clearTimeout(this.resizeTimeout)
        this.resizeTimeout = setTimeout(() => {
            this.viewportWidth = window.innerWidth
        }, 150)
    },
  },
  mounted() {
    window.addEventListener('resize', this.onResize, { passive: true })
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onResize)
    clearTimeout(this.resizeTimeout)
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

</style>
